import React from "react";
import L from "leaflet";
import {
  Map as LeafletMap,
  TileLayer,
  WMSTileLayer,
  GeoJSON,
  LayersControl,
  Pane,
} from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-markercluster";
import TreeDuringMovement from "./TreeDrawing/TreeDuringMovement";
import WMSCapabilities from "wms-capabilities";
import SelectedMapItemPolygon from "./SelectedMapItemPolygon/SelectedMapItemPolygon";
import RasterArea from "./RasterArea/RasterArea";
import BufferArea from "./BufferArea/BufferArea";
import WindTurbineFlowArea from "./WindTurbineFlowArea/WindTurbineFlowArea";
import TreeDrawing, { treeMarker } from "./TreeDrawing/TreeDrawing";
import EmissionDrawing from "./Emission/EmissionDrawing/EmissionDrawing";
import EmissionDrawingMovement from "./Emission/EmissionDrawing/EmissionDrawingMovement";
import WindTurbineDrawing from "./WindTurbine/WindTurbineDrawing/WindTurbineDrawing";
import WindTurbineDrawingMovement from "./WindTurbine/WindTurbineDrawing/WindTurbineDrawingMovement";
import MapPoiDrawing from "./MapPoi/MapPoiDrawing/MapPoiDrawing";
import MapPoiDrawingMovement from "./MapPoi/MapPoiDrawing/MapPoiDrawingMovement";
import { LayerOptions } from "../../common/TileLayers";
import { stylePolygon } from "../../utils/polygons/polygonStyle";
import RasterGeoJson from "./RasterArea/RasterGeoJson";
import NestedRasterGeoJson from "./RasterArea/NestedRasterGeoJson";
import ChildRasterGeoJson from "./RasterArea/ChildRasterGeoJson";
import TopographyWrapper from "./Topography/TopographyWrapper";
import ExtraTopography from "./Topography/ExtraTopography";
import PolygonDuringDrawing from "./PolygonDrawing/PolygonDuringDrawing";
import DrawnPolygon from "./PolygonDrawing/DrawnPolygon";
import DrawnPolygonGeoJson from "./PolygonDrawing/DrawnPolygonGeoJson";
import { ObjectPropCopies } from "./ObjectPropCopies/ObjectPropCopies";
import { SingleTreePropCopies } from "./ObjectPropCopies/SingleTreePropCopies";
import { EmissionPropCopies } from "./ObjectPropCopies/EmissionPropCopies";
import { WindTurbinePropCopies } from "./ObjectPropCopies/WindTurbinePropCopies";
import ChemistryGeoJson from "./ChemistryGeoJson/ChemistryGeoJson";
import WindTurbineGeoJson from "./WindTurbine/WindTurbineGeoJson/WindTurbineGeoJson";
import MapPoiGeoJson from "./MapPoi/MapPoiGeoJson/MapPoiGeoJson";
import LeafletRuler from "./WindTurbine/LeafletRuler";
import {
  addPolygonPointWg,
  drawPolygon,
  addTree,
  getCurrentCursorCoords,
  getSelectedMapItemGeoProps,
  centerMapSuccess,
  setWmsLayerNames,
  toggleCopyPropsMode,
  addAssignedObjectCopy,
  addAssignedSingleTreeCopy,
  addAssignedSingleEmissionCopy,
  addAssignedSingleWindTurbineCopy,
  filterGeoJson,
  toggleTreeCursorDisplay,
  enableTreeDraggable,
  disableTreeDraggable,
  addEmission,
  toggleEmissionCursorDisplay,
  enableEmissionDraggable,
  disableEmissionDraggable,
  addMapPoi,
  toggleMapPoiCursorDisplay,
  enableMapPoiDraggable,
  disableMapPoiDraggable,
  addWindTurbine,
  toggleWindTurbineCursorDisplay,
  enableWindTurbineDraggable,
  disableWindTurbineDraggable,
  setLayersControlExpanded,
} from "../../redux/actions/mapActions";
import { Loader } from "../Loader";
import { withTranslation } from "react-i18next";
import { connect, ReactReduxContext } from "react-redux";
import { getCombinedMapBounds } from "../../utils/mapBoundsUtils";
import {
  wmsCapabilityLayersFromXml,
  wmsCapabilityLayersToEntries,
} from "../../utils/wmsCapabilityLayers";
import "./Map.scss";
import { toggleTopographyVisibility } from "../../redux/actions/legendActions";
const { BaseLayer } = LayersControl;

class Map extends React.Component {
  static contextType = ReactReduxContext;

  constructor(props) {
    super(props);
    this.state = {};

    this.mapRef = React.createRef();
    this.geoJsonRef = React.createRef();
    this.drawnPolygonGeoJsonRef = React.createRef();
    this.selectedRef = React.createRef();
    this.rasterPreviewRef = React.createRef();
    this.parentRasterPreviewRef = React.createRef();
    this.childRasterPreviewRef = React.createRef();
    this.onEachFeature = this.onEachFeature.bind(this);
    this.handleMapItemClickGeojson = this.handleMapItemClickGeojson.bind(this);
    this.setInitialViewport = this.setInitialViewport.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.onMapClick = this.onMapClick.bind(this);
    this.handleRightClick = this.handleRightClick.bind(this);
    this.pointToLayer = this.pointToLayer.bind(this);
    this.handleZoomEnd = this.handleZoomEnd.bind(this);
    this.handleWmsBaseLayerChange = this.handleWmsBaseLayerChange.bind(this);
    this.tryFitMapToCaseGeotiff = this.tryFitMapToCaseGeotiff.bind(this);
    this._geotiffAutoCenterKey = null;
    this._geotiffFitTimer = null;
  }

  loadWmsCapabilities() {
    const url = this.props.currentWmsMap?.url;
    if (!url) {
      this.props.setWmsLayerNames([]);
      return;
    }
    const that = this;
    const sep = url.includes("?") ? "&" : "?";
    fetch(
      `${url}${sep}service=WMS&request=GetCapabilities&version=1.3.0`
    )
      .then(function (response) {
        return response.text();
      })
      .then(function (text) {
        let wmsLayersNames = wmsCapabilityLayersFromXml(text);
        if (!wmsLayersNames.length) {
          const result = new WMSCapabilities().parse(text);
          wmsLayersNames = wmsCapabilityLayersToEntries(result.Capability.Layer);
        }
        that.props.setWmsLayerNames(wmsLayersNames);
      });
  }

  componentDidMount() {
    if (this.props.currentWmsMap?.url) {
      const that = this;
      const rawUrl = `${this.props.currentWmsMap.url || ""}`.trim();
      if (!rawUrl) {
        return;
      }
      const urlCandidates = rawUrl.split(/\s+/).filter(Boolean);
      const normalizedUrl =
        urlCandidates.find((u) =>
          u.toLowerCase().includes("request=getcapabilities")
        ) || urlCandidates[0];
      const lower = normalizedUrl.toLowerCase();
      const hasCapabilitiesRequest =
        lower.includes("request=getcapabilities") &&
        lower.includes("service=wms");
      const separator = normalizedUrl.includes("?") ? "&" : "?";
      const capabilitiesUrl = hasCapabilitiesRequest
        ? normalizedUrl
        : `${normalizedUrl}${separator}service=WMS&request=GetCapabilities`;

      fetch(capabilitiesUrl)
        .then(function (response) {
          return response.text();
        })
        .then(function (text) {
          let wmsLayersNames = [];
          const result = new WMSCapabilities().parse(text);
          const multiLayer = result?.Capability?.Layer?.Layer;
          if (!Array.isArray(multiLayer)) {
            console.warn("Invalid WMS capabilities response.", {
              url: capabilitiesUrl,
              parsedResult: result,
            });
            return;
          }
          if (multiLayer.length === 1 && multiLayer[0].Layer) {
            multiLayer[0].Layer.forEach((x) => {
              wmsLayersNames.push({ name: x.Name, title: x.Title });
            });
          } else {
            multiLayer.forEach((x) => {
              wmsLayersNames.push({ name: x.Name, title: x.Title });
            });
          }
          that.props.setWmsLayerNames(wmsLayersNames);
        });
    }
    document.addEventListener("contextmenu", (event) => event.preventDefault());
    this.scheduleLayersControlObserver();
    this.scheduleTryFitMapToCaseGeotiff();
  }

  componentWillUnmount() {
    if (this._geotiffFitTimer != null) {
      clearTimeout(this._geotiffFitTimer);
      this._geotiffFitTimer = null;
    }
    this.teardownWmsBaselayerChangeHandler();
    this.teardownLayersControlObserver();
  }

  /** Defer one tick so SET_GEOJSON_WG can land before we auto-toggle topography. */
  scheduleTryFitMapToCaseGeotiff() {
    if (this._geotiffFitTimer != null) {
      clearTimeout(this._geotiffFitTimer);
    }
    this._geotiffFitTimer = window.setTimeout(() => {
      this._geotiffFitTimer = null;
      this.tryFitMapToCaseGeotiff();
    }, 0);
  }

  scheduleLayersControlObserver() {
    if (
      this.layersControlObserver ||
      this.layersControlAttachInterval != null ||
      this.layersControlAttachAbandoned
    ) {
      return;
    }
    const map = this.mapRef?.current?.leafletElement;
    if (!map) {
      return;
    }
    map.whenReady(() => {
      this.tryAttachLayersControlObserver(map);
      this.attachWmsBaselayerChangeHandlerOnce(map);
    });
  }

  attachWmsBaselayerChangeHandlerOnce(map) {
    if (this.wmsBaselayerChangeAttached || !map) {
      return;
    }
    this.wmsBaselayerChangeAttached = true;
    map.on("baselayerchange", this.handleWmsBaseLayerChange);
  }

  teardownWmsBaselayerChangeHandler() {
    const map = this.mapRef?.current?.leafletElement;
    if (map && this.wmsBaselayerChangeAttached) {
      map.off("baselayerchange", this.handleWmsBaseLayerChange);
    }
    this.wmsBaselayerChangeAttached = false;
  }

  handleWmsBaseLayerChange(e) {
    const layersOption =
      e.layer?.options?.layers ?? e.layer?.wmsParams?.layers;
    if (layersOption == null || !this.props.wmsLayersNames?.length) {
      return;
    }
    const layerName = String(layersOption).split(",")[0].trim();
    const entry = this.props.wmsLayersNames.find((x) => x.name === layerName);
    if (!entry?.bounds) {
      return;
    }
    const mapEl = this.mapRef?.current?.leafletElement;
    if (!mapEl) {
      return;
    }
    const llb = L.latLngBounds(entry.bounds[0], entry.bounds[1]);
    requestAnimationFrame(() => {
      const m = this.mapRef?.current?.leafletElement;
      if (!m || !llb.isValid()) {
        return;
      }
      m.fitBounds(llb, { maxZoom: 18, padding: [24, 24] });
    });
  }

  tryAttachLayersControlObserver(map) {
    if (this.layersControlObserver) {
      return;
    }
    const attach = () => {
      const layersEl = map
        .getContainer()
        .querySelector(".leaflet-control-layers");
      if (!layersEl) {
        return false;
      }

      this.layersControlPointerInside = false;
      const sync = () => {
        const expanded = layersEl.classList.contains(
          "leaflet-control-layers-expanded"
        );
        this.props.setLayersControlExpanded(
          expanded || this.layersControlPointerInside
        );
      };

      const onEnter = () => {
        this.layersControlPointerInside = true;
        sync();
      };
      const onLeave = () => {
        this.layersControlPointerInside = false;
        sync();
      };

      layersEl.addEventListener("mouseenter", onEnter);
      layersEl.addEventListener("mouseleave", onLeave);
      this.layersControlEl = layersEl;
      this.layersControlOnEnter = onEnter;
      this.layersControlOnLeave = onLeave;

      sync();
      const obs = new MutationObserver(sync);
      obs.observe(layersEl, { attributes: true, attributeFilter: ["class"] });
      this.layersControlObserver = obs;
      return true;
    };

    if (attach()) {
      return;
    }
    let attempts = 0;
    this.layersControlAttachInterval = window.setInterval(() => {
      attempts += 1;
      if (attach() || attempts > 40) {
        clearInterval(this.layersControlAttachInterval);
        this.layersControlAttachInterval = null;
        if (!this.layersControlObserver) {
          this.layersControlAttachAbandoned = true;
        }
      }
    }, 50);
  }

  teardownLayersControlObserver() {
    if (this.layersControlAttachInterval != null) {
      clearInterval(this.layersControlAttachInterval);
      this.layersControlAttachInterval = null;
    }
    if (this.layersControlObserver) {
      this.layersControlObserver.disconnect();
      this.layersControlObserver = null;
    }
    if (
      this.layersControlEl &&
      this.layersControlOnEnter &&
      this.layersControlOnLeave
    ) {
      this.layersControlEl.removeEventListener(
        "mouseenter",
        this.layersControlOnEnter
      );
      this.layersControlEl.removeEventListener(
        "mouseleave",
        this.layersControlOnLeave
      );
    }
    this.layersControlEl = null;
    this.layersControlOnEnter = null;
    this.layersControlOnLeave = null;
    this.layersControlPointerInside = false;
    this.props.setLayersControlExpanded(false);
  }

  componentDidUpdate(prevProps) {
    if (
      !this.layersControlObserver &&
      this.layersControlAttachInterval == null &&
      !this.layersControlAttachAbandoned
    ) {
      this.scheduleLayersControlObserver();
    }

    if (prevProps.currentWmsMap?.url !== this.props.currentWmsMap?.url) {
      this.loadWmsCapabilities();
    }

    if (
      prevProps.hiddenObjectTypesIndexes.length !==
      this.props.hiddenObjectTypesIndexes.length
    ) {
      this.props.filterGeoJson(this.props.hiddenObjectTypesIndexes);
      return;
    }

    if (!prevProps.isMapDataLoaded && this.props.isMapDataLoaded) {
      this.setInitialViewport();
      this.scheduleTryFitMapToCaseGeotiff();
    }

    if (
      prevProps.caseTopographyPath !== this.props.caseTopographyPath ||
      prevProps.topographyBounds !== this.props.topographyBounds
    ) {
      this.tryFitMapToCaseGeotiff();
    }

    if (
      !this.props.inRasterSettingsMode &&
      !prevProps.isCentering &&
      this.props.isCentering
    ) {
      this.setInitialViewport();
      this.props.centerMapSuccess();
    }
  }

  hasGeoJsonOrDrawnGeoJsonFeatures() {
    const main = this.props.geoJsonWg?.features;
    if (Array.isArray(main) && main.length > 0) {
      return true;
    }
    const drawn = this.props.drawnPolygonGeojsonWg?.features;
    return Array.isArray(drawn) && drawn.length > 0;
  }

  hasAnySavedMapData(state) {
    if (this.hasGeoJsonOrDrawnGeoJsonFeatures()) {
      return true;
    }

    const mapState = state?.map;
    if (!mapState) return false;

    const hasMainGeojson =
      !!mapState.geoJsonWg && Array.isArray(mapState.geoJsonWg.features) && mapState.geoJsonWg.features.length > 0;
    const hasDrawnPolygon =
      !!mapState.drawnPolygonGeojsonWg &&
      Array.isArray(mapState.drawnPolygonGeojsonWg.features) &&
      mapState.drawnPolygonGeojsonWg.features.length > 0;

    const hasPoints = (arr) => Array.isArray(arr) && arr.length > 0;
    const hasAnyPoints =
      hasPoints(mapState.allDrawnTreesCoordinatesWg) ||
      hasPoints(mapState.allDrawnEmissionCoordinatesWg) ||
      hasPoints(mapState.allDrawnMapPoiCoordinatesWg) ||
      hasPoints(mapState.allDrawnWindTurbineCoordinatesWg);

    return hasMainGeojson || hasDrawnPolygon || hasAnyPoints;
  }

  /**
   * When the case has a topography GeoTIFF but no saved GeoJSON / drawings,
   * show the layer and fit the map once bounds arrive from TopographyWrapper (async).
   */
  tryFitMapToCaseGeotiff() {
    const mapEl = this.mapRef?.current?.leafletElement;
    if (!mapEl) return;

    const store = this.context?.store;
    const state = store?.getState?.();
    if (!state) return;

    const casePath = this.props.caseTopographyPath;
    if (!casePath) return;

    if (this.hasAnySavedMapData(state)) return;

    const b = this.props.topographyBounds;
    if (!b || !Array.isArray(b) || b.length < 2) return;

    const llb = L.latLngBounds(b[0], b[1]);
    if (!llb.isValid()) return;

    const centerKey = `${casePath}|${JSON.stringify(b)}`;
    if (this._geotiffAutoCenterKey === centerKey) return;

    if (!state.legend?.topographyVisibility) {
      this.props.toggleTopographyVisibility();
    }

    const runFit = () => {
      const m = this.mapRef?.current?.leafletElement;
      if (!m || !llb.isValid()) return;
      try {
        m.invalidateSize(false);
      } catch (_e) {
        /* ignore */
      }
      m.fitBounds(llb, { maxZoom: 18, padding: [24, 24] });
      this._geotiffAutoCenterKey = centerKey;
    };

    mapEl.whenReady(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(runFit);
      });
    });
  }

  setInitialViewport() {
    const mapEl = this.mapRef?.current?.leafletElement;
    if (!mapEl) return;

    const store = this.context?.store;
    if (!store) return;

    const state = store.getState();
    const bounds = getCombinedMapBounds(state);
    if (bounds != null) {
      mapEl.fitBounds(bounds);
      return;
    }

    // Fallback: GeoTIFF bounds may arrive after this runs — tryFitMapToCaseGeotiff handles that.
    this.scheduleTryFitMapToCaseGeotiff();
  }

  onEachFeature(feature, layer) {
    layer.on({
      click: this.handleMapItemClickGeojson,
    });
  }

  handleMapItemClickGeojson(e) {
    if (
      this.props.inTreeDrawingMode ||
      this.props.inDrawingMode ||
      this.props.inPolygonDrawingMode ||
      this.props.inCopyPropsMode
    ) {
      return;
    } else if (
      this.props.inSingleTreeCopyPropsMode ||
      this.props.inEmissionCopyPropsMode ||
      this.props.inWindTurbineCopyPropsMode
    ) {
      let itemFeature = e.target.feature;
      if (itemFeature.properties.t === 6) {
        this.props.addAssignedSingleTreeCopy(itemFeature);
      } else if (itemFeature.properties.t === 8) {
        this.props.addAssignedSingleEmissionCopy(itemFeature);
      } else if (itemFeature.properties.t === 10) {
        this.props.addAssignedSingleWindTurbineCopy(itemFeature);
      }
      return;
    }

    this.props.getSelectedMapItemGeoProps(e.target.feature);
  }

  onMapClick = (e) => {
    const coordinates = e.latlng;
    if (this.props.inCopyPropsMode) {
      this.props.addAssignedObjectCopy([
        coordinates.lng.toFixed(6),
        coordinates.lat.toFixed(6),
      ]);
    } else if (this.props.inTreeDrawingMode && this.props.displayTreeCursor) {
      this.props.addTree([
        coordinates.lat.toFixed(6),
        coordinates.lng.toFixed(6),
      ]);
    } else if (this.props.inPolygonDrawingMode) {
      this.props.addPolygonPointWg({
        lng: Number(coordinates.lng.toFixed(6)),
        lat: Number(coordinates.lat.toFixed(6)),
      });
    } else if (
      this.props.inEmissionDrawingMode &&
      this.props.displayEmissionCursor
    ) {
      this.props.addEmission([
        coordinates.lat.toFixed(6),
        coordinates.lng.toFixed(6),
      ]);
    } else if (
      this.props.inWindTurbineDrawingMode &&
      this.props.displayWindTurbineCursor
    ) {
      this.props.addWindTurbine([
        coordinates.lat.toFixed(6),
        coordinates.lng.toFixed(6),
      ]);
    } else if (
      this.props.inMapPoiDrawingMode &&
      this.props.displayMapPoiCursor
    ) {
      this.props.addMapPoi([
        coordinates.lat.toFixed(6),
        coordinates.lng.toFixed(6),
      ]);
    }
  };

  handleMouseMove = (e) => {
    this.props.getCurrentCursorCoords([
      e.latlng.lat.toFixed(6),
      e.latlng.lng.toFixed(6),
    ]);
  };

  handleRightClick() {
    if (this.props.inDrawingMode) {
      if (this.props.inPolygonDrawingMode) {
        this.props.drawPolygon();
      } else if (this.props.inTreeDrawingMode) {
        this.props.toggleTreeCursorDisplay();
      } else if (this.props.inEmissionDrawingMode) {
        this.props.toggleEmissionCursorDisplay();
      } else if (this.props.inWindTurbineDrawingMode) {
        this.props.toggleWindTurbineCursorDisplay();
      } else if (this.props.inMapPoiDrawingMode) {
        this.props.toggleMapPoiCursorDisplay();
      }
    }
  }

  handleZoomEnd() {
    if (this.props.inDrawingMode) {
      if (this.props.inTreeDrawingMode) {
        if (this.mapRef.current.leafletElement.getZoom() === 19) {
          this.props.enableTreeDraggable();
        } else {
          this.props.disableTreeDraggable();
        }
      } else if (this.props.inEmissionDrawingMode) {
        if (this.mapRef.current.leafletElement.getZoom() === 19) {
          this.props.enableEmissionDraggable();
        } else {
          this.props.disableEmissionDraggable();
        }
      } else if (this.props.inWindTurbineDrawingMode) {
        if (this.mapRef.current.leafletElement.getZoom() === 19) {
          this.props.enableWindTurbineDraggable();
        } else {
          this.props.disableWindTurbineDraggable();
        }
      } else if (this.props.inMapPoiDrawingMode) {
        if (this.mapRef.current.leafletElement.getZoom() === 19) {
          this.props.enableMapPoiDraggable();
        } else {
          this.props.disableMapPoiDraggable();
        }
      }
    }
  }

  pointToLayer(feature, latlng) {
    return L.marker(latlng, { icon: treeMarker });
  }

  render() {
    const { t } = this.props;
    return (
      <>
        {!this.props.isMapDataLoaded && <Loader />}
        <LeafletMap
          ref={this.mapRef}
          className={
            this.props.isMapDataLoaded
              ? "leaflet-map"
              : "leaflet-map leaflet-map-hidden"
          }
          maxNativeZoom={19}
          maxZoom={24}
          center={this.props.center}
          zoom={this.props.zoom}
          preferCanvas
          zoomControl
          onClick={this.onMapClick}
          onMouseMove={
            this.props.inPolygonDrawingMode ||
              (this.props.inTreeDrawingMode && this.props.displayTreeCursor) ||
              (this.props.inEmissionDrawingMode &&
                this.props.displayEmissionCursor) ||
              (this.props.inWindTurbineDrawingMode &&
                this.props.displayWindTurbineCursor) ||
              (this.props.inMapPoiDrawingMode && this.props.displayMapPoiCursor)
              ? this.handleMouseMove
              : null
          }
          onContextMenu={this.handleRightClick}
          onZoomend={this.handleZoomEnd}
        >
          <LayersControl position="bottomleft">
            {LayerOptions.map((x) => {
              return (
                <BaseLayer key={x.name} name={t(x.name)} checked={x.checked}>
                  <TileLayer attribution={x.attribution} url={x.url} />
                </BaseLayer>
              );
            })}
            {this.props.wmsLayersNames.length > 0 &&
              this.props.wmsLayersNames.map((x) => (
                <BaseLayer key={x.name} name={x.title} checked={false}>
                  <WMSTileLayer
                    url={this.props.currentWmsMap.url}
                    layers={x.name}
                  />
                </BaseLayer>
              ))}
          </LayersControl>
          {!this.props.inTreeDrawingMode && (
            <TreeDrawing mapRef={this.mapRef} />
          )}
          {this.props.inTreeDrawingMode && (
            <>
              <TreeDuringMovement />
              <TreeDrawing mapRef={this.mapRef} />
            </>
          )}
          {this.props.inEmissionDrawingMode && (
            <>
              <EmissionDrawingMovement />
              <EmissionDrawing mapRef={this.mapRef} />
            </>
          )}
          {!this.props.inEmissionDrawingMode && (
            <EmissionDrawing mapRef={this.mapRef} />
          )}
          <MapPoiDrawing mapRef={this.mapRef} />
          {this.props.inMapPoiDrawingMode && <MapPoiDrawingMovement />}
          {/* <WindTurbineDrawing mapRef={this.mapRef} /> */}
          {!this.props.inWindTurbineDrawingMode && (
            <WindTurbineDrawing mapRef={this.mapRef} />
          )}
          {this.props.inWindTurbineDrawingMode && (
            <>
              <WindTurbineDrawing mapRef={this.mapRef} />
              <WindTurbineDrawingMovement />
            </>
          )}
          {this.props.inSingleTreeCopyPropsMode && <SingleTreePropCopies />}
          {this.props.inEmissionCopyPropsMode && <EmissionPropCopies />}
          {this.props.inWindTurbineCopyPropsMode && <WindTurbinePropCopies />}
          {/*{this.props.geoJsonWg && (
            <GeojsonLayer
              geoKey={this.props.geoKey}
              geoJsonRef={this.geoJsonRef}
              pointToLayer={this.pointToLayer}
              onEachFeature={this.onEachFeature}
            />
          )}*/}
          {!!this.props.geoJsonWg &&
            this.props.isGeoJsonVisible &&
            this.props.geoJsonWg?.features.length !== 0 && (
              <MarkerClusterGroup
                key={this.props.geoKey}
                disableClusteringAtZoom={19}
                showCoverageOnHover
                zoomToBoundsOnClick
                spiderfyOnMaxZoom={false}
              >
                <GeoJSON
                  ref={this.geoJsonRef}
                  style={(x) => {
                    return stylePolygon(
                      x,
                      false,
                      this.props.inputPalmTypesPalette
                    );
                  }}
                  data={
                    !this.props.hiddenObjectTypesIndexes.length
                      ? this.props.geoJsonWg
                      : this.props.filteredGeoJson
                  }
                  onEachFeature={this.onEachFeature}
                  pointToLayer={this.pointToLayer}
                />
              </MarkerClusterGroup>
            )}
          {this.props.isRasterPreviewVisible && (
            <RasterGeoJson rasterPreviewRef={this.rasterPreviewRef} />
          )}
          {this.props.isParentRasterPreviewVisible && (
            <NestedRasterGeoJson
              parentRasterPreviewRef={this.parentRasterPreviewRef}
              mapRef={this.mapRef}
            />
          )}
          {this.props.isChildRasterPreviewVisible && (
            <ChildRasterGeoJson
              childRasterPreviewRef={this.childRasterPreviewRef}
              mapRef={this.mapRef}
            />
          )}
          {this.props.inCopyPropsMode && (
            <Pane zIndex={2}>
              <ObjectPropCopies />
            </Pane>
          )}
          {this.props.inPolygonDrawingMode && (
            <Pane>
              <DrawnPolygon />
              <PolygonDuringDrawing />
            </Pane>
          )}
          {!this.props.inPolygonDrawingMode && (
            <DrawnPolygonGeoJson
              drawnPolygonGeoJsonRef={this.drawnPolygonGeoJsonRef}
              onEachFeature={this.onEachFeature}
            />
          )}
          <ChemistryGeoJson onEachFeature={this.onEachFeature} />
          <WindTurbineGeoJson onEachFeature={this.onEachFeature} />
          <MapPoiGeoJson onEachFeature={this.onEachFeature} />
          {this.props.inRasterSettingsMode && (
            <>
              <BufferArea />
              <WindTurbineFlowArea />
              <RasterArea
                mapRef={this.mapRef}
                geoJsonRef={this.geoJsonRef}
                drawnPolygonGeoJsonRef={this.drawnPolygonGeoJsonRef}
              />
            </>
          )}
          <TopographyWrapper />
          <ExtraTopography />
          <SelectedMapItemPolygon layerRef={this.selectedRef} />
          <LeafletRuler style={{ top: "380px" }} />
        </LeafletMap>
      </>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    geoJsonWg: state.map.geoJsonWg,
    drawnPolygonGeojsonWg: state.map.drawnPolygonGeojsonWg,
    isGeoJsonVisible: state.rasterArea.isGeoJsonVisible,
    parentRasterPreview: state.rasterArea.parentRasterPreview,
    childRasterPreview: state.rasterArea.childRasterPreview,
    isParentRasterPreviewVisible: state.rasterArea.isParentRasterPreviewVisible,
    isChildRasterPreviewVisible: state.rasterArea.isChildRasterPreviewVisible,
    isRasterPreviewVisible: state.rasterArea.isRasterPreviewVisible,
    hiddenObjectTypesIndexes: state.legend.hiddenObjectTypesIndexes,
    inTreeDrawingMode: state.map.inTreeDrawingMode,
    inRasterSettingsMode: state.map.inRasterSettingsMode,
    inPolygonDrawingMode: state.map.inPolygonDrawingMode,
    inDrawingMode: state.map.inDrawingMode,
    isCentering: state.map.isCentering,
    currentWmsMap: state.userSettings?.settings?.wsMaps?.currentValue,
    wmsLayersNames: state.map.wmsLayersNames,
    inCopyPropsMode: state.map.inCopyPropsMode,
    inSingleTreeCopyPropsMode: state.map.inSingleTreeCopyPropsMode,
    geoKey: state.map.geoKey,
    filteredGeoJson: state.map.filteredGeoJson,
    inputPalmTypesPalette:
      state.userSettings?.settings?.visualizationPresets
        ?.inputPalmTypesPalette || {},
    displayTreeCursor: state.map.displayTreeCursor,
    inEmissionDrawingMode: state.map.inEmissionDrawingMode,
    displayEmissionCursor: state.map.displayEmissionCursor,
    displayMapPoiCursor: state.map.displayMapPoiCursor,
    inMapPoiDrawingMode: state.map.inMapPoiDrawingMode,
    inEmissionCopyPropsMode: state.map.inEmissionCopyPropsMode,
    inWindTurbineDrawingMode: state.map.inWindTurbineDrawingMode,
    displayWindTurbineCursor: state.map.displayWindTurbineCursor,
    inWindTurbineCopyPropsMode: state.map.inWindTurbineCopyPropsMode,
    isMapDataLoaded: state.map.isMapDataLoaded,
    caseTopographyPath: state.projects?.currentCase?.topographyUrl ?? null,
    topographyBounds: state.legend.topographyBounds ?? null,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    addPolygonPointWg: (payload) => dispatch(addPolygonPointWg(payload)),
    drawPolygon: () => dispatch(drawPolygon()),
    filterGeoJson: (payload) => dispatch(filterGeoJson(payload)),
    addTree: (payload) => dispatch(addTree(payload)),
    getCurrentCursorCoords: (payload) =>
      dispatch(getCurrentCursorCoords(payload)),
    getSelectedMapItemGeoProps: (payload) =>
      dispatch(getSelectedMapItemGeoProps(payload)),
    centerMapSuccess: () => dispatch(centerMapSuccess()),
    setWmsLayerNames: (payload) => dispatch(setWmsLayerNames(payload)),
    toggleCopyPropsMode: () => dispatch(toggleCopyPropsMode()),
    addAssignedObjectCopy: (payload) =>
      dispatch(addAssignedObjectCopy(payload)),
    addAssignedSingleTreeCopy: (payload) =>
      dispatch(addAssignedSingleTreeCopy(payload)),
    addAssignedSingleEmissionCopy: (payload) =>
      dispatch(addAssignedSingleEmissionCopy(payload)),
    toggleTreeCursorDisplay: () => dispatch(toggleTreeCursorDisplay()),
    enableTreeDraggable: () => dispatch(enableTreeDraggable()),
    disableTreeDraggable: () => dispatch(disableTreeDraggable()),
    addEmission: (payload) => dispatch(addEmission(payload)),
    toggleEmissionCursorDisplay: () => dispatch(toggleEmissionCursorDisplay()),
    enableEmissionDraggable: () => dispatch(enableEmissionDraggable()),
    disableEmissionDraggable: () => dispatch(disableEmissionDraggable()),
    addMapPoi: (payload) => dispatch(addMapPoi(payload)),
    toggleMapPoiCursorDisplay: () => dispatch(toggleMapPoiCursorDisplay()),
    enableMapPoiDraggable: () => dispatch(enableMapPoiDraggable()),
    disableMapPoiDraggable: () => dispatch(disableMapPoiDraggable()),
    addWindTurbine: (payload) => dispatch(addWindTurbine(payload)),
    toggleWindTurbineCursorDisplay: (payload) =>
      dispatch(toggleWindTurbineCursorDisplay(payload)),
    enableWindTurbineDraggable: (payload) =>
      dispatch(enableWindTurbineDraggable(payload)),
    disableWindTurbineDraggable: (payload) =>
      dispatch(disableWindTurbineDraggable(payload)),
    addAssignedSingleWindTurbineCopy: (payload) =>
      dispatch(addAssignedSingleWindTurbineCopy(payload)),
    setLayersControlExpanded: (payload) =>
      dispatch(setLayersControlExpanded(payload)),
    toggleTopographyVisibility: () => dispatch(toggleTopographyVisibility()),
  };
};

export default withTranslation()(
  connect(mapStateToProps, mapDispatchToProps)(Map)
);
